import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from 'react-leaflet';
import { getAccessToken } from '../utils/auth';
import { getTrackAnomalies, reviewTrackAnomaly, getTrackPoints, backfillAnomalies } from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';
import 'leaflet/dist/leaflet.css';
import './TrackAnomalies.css';

const ANOMALY_TYPES = ['', 'HOLDING', 'GO_AROUND', 'EXTENDED_APPROACH', 'DATA_ERROR', 'TRAINING_CIRCUITS'];
const ANOMALY_COLORS = {
  HOLDING: '#ff9800',
  GO_AROUND: '#f44336',
  EXTENDED_APPROACH: '#2196f3',
  DATA_ERROR: '#9e9e9e',
  TRAINING_CIRCUITS: '#9c27b0',
};

function getAltitudeColor(altitude) {
  const alt = Math.max(0, Math.min(altitude, 45000));
  if (alt < 5000) {
    const t = alt / 5000;
    return `rgb(${Math.round(50 + t * 200)}, ${Math.round(200 - t * 100)}, 50)`;
  }
  const t = Math.min((alt - 5000) / 35000, 1);
  return `rgb(${Math.round(255 - t * 100)}, ${Math.round(100 - t * 80)}, ${Math.round(t * 50)})`;
}

function TrackAnomalies() {
  const [anomalies, setAnomalies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);

  // Filters
  const [airportFilter, setAirportFilter] = useState('EIDW');
  const [typeFilter, setTypeFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState(null);

  // Selected anomaly + track
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [trackPoints, setTrackPoints] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  // Review state
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewMessage, setReviewMessage] = useState(null);

  const fetchAnomalies = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await getTrackAnomalies({
        airportIcao: airportFilter,
        anomalyType: typeFilter || undefined,
        reviewed: reviewedFilter,
        page,
        limit,
      }, token);

      if (isApiSuccess(response)) {
        setAnomalies(response.anomalies || []);
        setTotal(response.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch anomalies:', e);
    }
    setLoading(false);
  }, [airportFilter, typeFilter, reviewedFilter, page, limit]);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  const handleSelectAnomaly = async (anomaly) => {
    if (selectedAnomaly?.id === anomaly.id) {
      setSelectedAnomaly(null);
      setTrackPoints(null);
      return;
    }

    setSelectedAnomaly(anomaly);
    setTrackPoints(null);
    setReviewFeedback('');
    setReviewMessage(null);

    // Load track if we have trackId
    if (anomaly.trackId) {
      setTrackLoading(true);
      try {
        const token = getAccessToken();
        const response = await getTrackPoints(anomaly.trackId, 5, token);
        if (isApiSuccess(response) && response.points) {
          setTrackPoints(response.points);
        }
      } catch (e) {
        console.error('Failed to load track:', e);
      }
      setTrackLoading(false);
    }
  };

  const handleReview = async (correct) => {
    if (!selectedAnomaly) return;

    try {
      const token = getAccessToken();
      const response = await reviewTrackAnomaly(
        selectedAnomaly.id, correct,
        correct ? '' : reviewFeedback,
        token
      );

      if (isApiSuccess(response)) {
        setReviewMessage({ type: 'success', text: `Marked as ${correct ? 'correct' : 'incorrect'}` });
        fetchAnomalies();
      } else {
        setReviewMessage({ type: 'error', text: 'Review failed' });
      }
    } catch (e) {
      setReviewMessage({ type: 'error', text: e.message });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="track-anomalies">
      {/* Filters */}
      <div className="anomaly-filters">
        <select value={airportFilter} onChange={e => { setAirportFilter(e.target.value); setPage(0); }}>
          <option value="EIDW">EIDW (Dublin)</option>
          <option value="LPPR">LPPR (Porto)</option>
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
          <option value="">All Types</option>
          {ANOMALY_TYPES.filter(t => t).map(t => (
            <option key={t} value={t}>{t.replace('_', ' ')}</option>
          ))}
        </select>
        <select value={reviewedFilter === null ? '' : reviewedFilter.toString()}
                onChange={e => { setReviewedFilter(e.target.value === '' ? null : e.target.value === 'true'); setPage(0); }}>
          <option value="">All Status</option>
          <option value="false">Unreviewed</option>
          <option value="true">Reviewed</option>
        </select>
        <span className="anomaly-count">{total} anomalies</span>
        <button className="backfill-btn" onClick={async () => {
          const token = getAccessToken();
          if (!token) return;
          const btn = document.querySelector('.backfill-btn');
          btn.disabled = true;
          btn.textContent = 'Backfilling...';
          try {
            const result = await backfillAnomalies(airportFilter, 14, token);
            if (isApiSuccess(result)) {
              btn.textContent = `Done: ${result.goAroundsFound} GA, ${result.holdingPatternsFound} holds`;
              fetchAnomalies();
            } else {
              btn.textContent = 'Error';
            }
          } catch (e) {
            btn.textContent = 'Error';
          }
          setTimeout(() => { btn.disabled = false; btn.textContent = 'Backfill 14d'; }, 5000);
        }}>Backfill 14d</button>
      </div>

      {/* Table */}
      <div className="anomaly-table-container">
        <table className="anomaly-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Callsign</th>
              <th>Type</th>
              <th>Anomaly</th>
              <th>Error</th>
              <th>Cause</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
            ) : anomalies.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No anomalies found</td></tr>
            ) : anomalies.map(a => (
              <tr key={a.id}
                  className={`anomaly-row ${selectedAnomaly?.id === a.id ? 'selected' : ''}`}
                  onClick={() => handleSelectAnomaly(a)}>
                <td>{a.flightDate || '-'}</td>
                <td className="callsign-cell">{a.callsign || '-'}</td>
                <td>{a.aircraftType || '-'}</td>
                <td>
                  <span className="anomaly-badge" style={{backgroundColor: ANOMALY_COLORS[a.anomalyType] || '#666'}}>
                    {a.anomalyType?.replace('_', ' ')}
                  </span>
                </td>
                <td className="numeric-cell">
                  {a.absError != null ? `${a.absError.toFixed(1)}m` : '-'}
                </td>
                <td className="cause-cell" title={a.causeDescription}>
                  {a.causeDescription?.substring(0, 60) || '-'}
                  {a.causeDescription?.length > 60 ? '...' : ''}
                </td>
                <td>
                  {a.reviewed ? (
                    <span className={`review-badge ${a.reviewCorrect ? 'correct' : 'incorrect'}`}>
                      {a.reviewCorrect ? '✓' : '✗'}
                    </span>
                  ) : (
                    <span className="review-badge pending">?</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="anomaly-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      {/* Detail Panel */}
      {selectedAnomaly && (
        <div className="anomaly-detail">
          <div className="detail-header">
            <h3>{selectedAnomaly.callsign} — {selectedAnomaly.anomalyType?.replace('_', ' ')}</h3>
            <button className="close-btn" onClick={() => { setSelectedAnomaly(null); setTrackPoints(null); }}>×</button>
          </div>

          <div className="detail-content">
            {/* Map */}
            <div className="detail-map">
              {trackLoading ? (
                <div className="map-loading">Loading track...</div>
              ) : trackPoints && trackPoints.length > 0 ? (
                <MapContainer
                  key={selectedAnomaly.id}
                  center={[trackPoints[Math.floor(trackPoints.length / 2)].latitude,
                           trackPoints[Math.floor(trackPoints.length / 2)].longitude]}
                  zoom={8}
                  style={{ height: '400px', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {/* Track polyline */}
                  {trackPoints.map((pt, i) => {
                    if (i >= trackPoints.length - 1) return null;
                    const next = trackPoints[i + 1];
                    const avgAlt = ((pt.altitude || 0) + (next.altitude || 0)) / 2;
                    return (
                      <Polyline
                        key={i}
                        positions={[[pt.latitude, pt.longitude], [next.latitude, next.longitude]]}
                        pathOptions={{ color: getAltitudeColor(avgAlt), weight: 2, opacity: 0.9 }}
                      />
                    );
                  })}
                  {/* Deviation point marker */}
                  {selectedAnomaly.deviationPointLat && (
                    <CircleMarker
                      center={[selectedAnomaly.deviationPointLat, selectedAnomaly.deviationPointLon]}
                      radius={8}
                      pathOptions={{ color: '#ff0000', fillColor: '#ff0000', fillOpacity: 0.8 }}
                    >
                      <Popup>Deviation Point</Popup>
                    </CircleMarker>
                  )}
                </MapContainer>
              ) : (
                <div className="map-empty">
                  {selectedAnomaly.trackId
                    ? 'No track points stored for this track'
                    : 'No track linked to this anomaly — live holds recorded before this was fixed carry no track id'}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="detail-info">
              <div className="info-grid">
                <div><strong>Predicted:</strong> {selectedAnomaly.predictedMinutes?.toFixed(1)} min</div>
                <div><strong>Actual:</strong> {selectedAnomaly.actualMinutes?.toFixed(1)} min</div>
                <div><strong>Error:</strong> {selectedAnomaly.absError?.toFixed(1)} min</div>
                <div><strong>Aircraft:</strong> {selectedAnomaly.aircraftType}</div>
                {selectedAnomaly.holdDurationMinutes > 0 && (
                  <div><strong>Holding Time:</strong> {selectedAnomaly.holdDurationMinutes?.toFixed(1)} min</div>
                )}
                {/* Loops keeps the fraction: 1.5 means the aircraft was released
                    half way round. holdOrbitCount is the old truncated integer. */}
                {selectedAnomaly.holdLoops > 0 ? (
                  <div><strong>Loops:</strong> {selectedAnomaly.holdLoops?.toFixed(2)}</div>
                ) : selectedAnomaly.holdOrbitCount > 0 && (
                  <div><strong>Orbits:</strong> {selectedAnomaly.holdOrbitCount}</div>
                )}
                {selectedAnomaly.holdEntryAltitudeFt != null && (
                  <div><strong>Entry Alt:</strong> {selectedAnomaly.holdEntryAltitudeFt.toLocaleString()} ft</div>
                )}
                {selectedAnomaly.holdExitAltitudeFt != null && (
                  <div><strong>Exit Alt:</strong> {selectedAnomaly.holdExitAltitudeFt.toLocaleString()} ft</div>
                )}
                {selectedAnomaly.holdingPatternName && (
                  <div><strong>Pattern:</strong> {selectedAnomaly.holdingPatternName}</div>
                )}
                {selectedAnomaly.nearestFixName && (
                  <div><strong>Nearest Fix:</strong> {selectedAnomaly.nearestFixName}</div>
                )}
              </div>
              <div className="cause-full">
                <strong>Analysis:</strong> {selectedAnomaly.causeDescription}
              </div>

              {/* Review */}
              <div className="review-section">
                <div className="review-buttons">
                  <button className="review-btn correct" onClick={() => handleReview(true)}>
                    Correct
                  </button>
                  <button className="review-btn incorrect" onClick={() => handleReview(false)}>
                    Incorrect
                  </button>
                </div>
                <textarea
                  className="review-feedback"
                  placeholder="Feedback (required if incorrect)..."
                  value={reviewFeedback}
                  onChange={e => setReviewFeedback(e.target.value)}
                />
                {reviewMessage && (
                  <div className={`review-message ${reviewMessage.type}`}>
                    {reviewMessage.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackAnomalies;
